import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet';

import { splitStringArray } from '~/utils/splitStringArray';

import { SeoProps, SeoQuery } from './models';

const Seo: FC<SeoProps> = ({
  locale,
  urls,
  seo: {
    title,
    description,
    keywords,
    externalHreflangs,
    additionalMeta,
    canonicalUrl,
    pageType = 'website',
  },
  pageUid,
  fullPath,
  openGraph,
  mainImagePath,
}) => {
  const { href: currentUrl } = useLocation();

  const {
    allContentstackSettings: { nodes },
    site: {
      siteMetadata: { siteUrl },
    },
    sitePlugin,
    hkxPlugin,
  } = useStaticQuery<SeoQuery>(graphql`
    query Seo {
      allContentstackSettings {
        nodes {
          siteName: site_name
          defaultLocale: default_locale
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
      site {
        siteMetadata {
          siteUrl
        }
      }
      sitePlugin(name: { eq: "default-site-plugin" }) {
        packageJson
      }
      hkxPlugin: sitePlugin(name: { eq: "gatsby-theme-hcp" }) {
        packageJson
      }
    }
  `);

  const { siteName, defaultLocale } =
    nodes.find(
      ({ tags, publishDetails: { locale: publishLocale } }) =>
        tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
        publishLocale === locale
    ) || {};

  const titleTemplate = title === siteName ? '%s' : `%s | ${siteName}`;
  const siteLang = locale || defaultLocale;

  const meta = [
    {
      name: 'description',
      content: description,
    },
    {
      property: 'og:url',
      content: currentUrl,
    },
    {
      property: 'og:title',
      content: openGraph?.title || title,
    },
    {
      property: 'og:description',
      content: openGraph?.description || description,
    },

    {
      property: 'og:image',
      content: openGraph?.image?.url || mainImagePath,
    },
    {
      property: 'og:type',
      content: pageType,
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
    {
      name: 'twitter:creator',
      content: process.env.MANIFEST_BRAND,
    },
    {
      name: 'twitter:title',
      content: openGraph?.title || title,
    },
    {
      name: 'twitter:description',
      content: openGraph?.description || description,
    },
    {
      property: 'twitter:image',
      content: openGraph?.image?.url || mainImagePath,
    },
    ...(keywords?.length
      ? [
          {
            name: 'keywords',
            content: splitStringArray(keywords).join(', '),
          },
        ]
      : []),
    ...additionalMeta.map(({ content, name, property }) => ({
      ...(name && { name }),
      ...(property && { property }),
      content,
    })),
  ].filter((item) => Boolean(item.content));

  const link = fullPath
    ? [
        {
          rel: 'canonical',
          href: canonicalUrl || `${siteUrl}${fullPath}`,
        },
        ...urls.map(({ locale: hreflang, url }) => ({
          rel: 'alternate',
          href: `${siteUrl}${url}`,
          hreflang,
        })),
        ...externalHreflangs.map(({ hreflang, url: { href } }) => ({
          rel: 'alternate',
          href,
          hreflang,
        })),
      ]
    : null;

  return (
    // TODO: replace helmet with buildin Head feature, to rethink - https://dev.azure.com/RB-Group/IT_and_D_Project/_workitems/edit/249204
    <Helmet
      htmlAttributes={{
        lang: siteLang,
        'data-page-uid': pageUid,
        'data-hkx-ver':
          hkxPlugin?.packageJson.version || sitePlugin.packageJson.version,
      }}
      {...{ title, titleTemplate, meta, link }}
    />
  );
};

export default Seo;
